// Central query-key factory. One shape per backend resource so every route /
// component that needs the same data subscribes to the SAME key and shares one
// fetch. Keys are built hierarchically (`events` → `events,list` → …) so a
// single `invalidateQueries({ queryKey: qk.events.all })` busts everything
// event-related after a mutation.
//
// `as const` keeps the tuples literal so TanStack Query's structural matching is
// exact and (in TS) the keys are autocompleted at call sites.

export type EventListFilters = {
  page?: number;
  limit?: number;
  upcoming?: boolean;
};

export const qk = {
  session: ['session'] as const,

  events: {
    all: ['events'] as const,
    list: (filters: EventListFilters = {}) => ['events', 'list', filters] as const,
    detail: (id: string) => ['events', 'detail', id] as const,
    stats: (eventIds: string[]) => ['events', 'stats', [...eventIds].sort()] as const,
  },

  orders: {
    all: ['orders'] as const,
    list: (filters: Record<string, unknown> = {}) => ['orders', 'list', filters] as const,
    detail: (id: string) => ['orders', 'detail', id] as const,
  },

  payouts: {
    all: ['payouts'] as const,
    list: () => ['payouts', 'list'] as const,
    detail: (id: string) => ['payouts', 'detail', id] as const,
  },

  // Split payments & instalment plans. An "arrangement" is one order being collected in parts —
  // by several people (a split) or over time (a plan). One key space for both, because the
  // backend treats them as one mechanism with a different variable.
  paymentPlans: {
    all: ['splits'] as const,
    // `filters` includes the time window: it changes which rows exist, not just how they are
    // shown, so two windows must never share a cache entry.
    arrangements: (filters: Record<string, unknown> = {}) => ['paymentPlans', 'arrangements', filters] as const,
    arrangement: (id: string) => ['paymentPlans', 'arrangement', id] as const,
    events: () => ['paymentPlans', 'events'] as const,
    event: (id: string) => ['paymentPlans', 'event', id] as const,
    // Keyed by BOTH ids: the resolved policy differs per tier (a tier may only tighten its
    // event), so an event-level answer must not be served under a tier-level key.
    policy: (eventId?: string | null, ticketTypeId?: string | null) =>
      ['paymentPlans', 'policy', eventId || null, ticketTypeId || null] as const,
    savedViews: () => ['paymentPlans', 'savedViews'] as const,
  },

  organizers: {
    all: ['organizers'] as const,
    list: (filters: Record<string, unknown> = {}) => ['organizers', 'list', filters] as const,
    detail: (id: string) => ['organizers', 'detail', id] as const,
    // Heavier than `detail` (which is also seeded with lightweight directory
    // rows) — the full organization+members+invitations+events+stats payload.
    // A DISTINCT key so a lightweight seeded row is never mistaken for it.
    fullDetail: (id: string) => ['organizers', 'fullDetail', id] as const,
  },

  finance: {
    all: ['finance'] as const,
    stats: (window: Record<string, unknown> = {}) => ['finance', 'stats', window] as const,
  },

  dashboard: {
    all: ['dashboard'] as const,
    data: (financeWindow: Record<string, unknown> | null = null) =>
      ['dashboard', 'data', financeWindow] as const,
  },
} as const;
